//** Import Components */
import { useState } from "react";
import axiosWithAuth from "../utils/axiosWithAuth";
import {
  Form,
  Input,
  Modal,
  Button,
  message,
  Table,
  Tooltip,
  Empty,
  Space,
  InputNumber,
  Checkbox,
  Collapse,
  Grid,
  Pagination,
} from "antd";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { TABLE_DATE_FORMAT } from "../common/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit } from "@fortawesome/free-solid-svg-icons";

export const useManageAddons = ({
  itemType,
  type,
  creatorID,
  user,
  modalData,
  dispatchAction,
  deleteItemAPI,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [modalForm] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();

  //* Function to create new item
  const createListItem = (values) => {
    const APIURL = `/api/restaurant/extra/createExtraRestaurant`;

    const listingData = {
      restaurant_id: creatorID,
      name: values.name,
      price: values.price,
      description: values.description,
      is_available: values.is_available,
    };

    axiosWithAuth()
      .post(APIURL, listingData)
      .then((res) => {
        console.log(res.data);

        let config = {
          content: `New addon created!`,
          style: {
            background: "none",
          },
        };
        message.success(config);

        closeModal();
        setIsSubmitting(false);
        dispatch(dispatchAction);
        modalForm.resetFields();
      })
      .catch((err) => {
        console.log(err.response);
        message.error(err.message);
        setIsSubmitting(false);
      });
  };

  //* Function to delete a list item
  const deleteListItem = (APIURL) => {
    if (window.confirm("Are you sure you wish to delete this item?")) {
      axiosWithAuth()
        .delete(APIURL)
        .then((res) => {
          dispatch(dispatchAction);
          console.log(res.data, APIURL);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  //* functions to handle modal
  const showModal = () => {
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const submitModalForm = () => {
    setIsSubmitting(true);

    modalForm.submit();
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setIsSubmitting(false);
  };

  const isAvailable = (e) => {
    // setIsFreeTicket(e.target.checked);

    if (e.target.checked) {
      modalForm.setFieldsValue({
        is_available: true,
      });
    } else {
      modalForm.setFieldsValue({
        is_available: false,
      });
    }
  };

  //* Component that holds the modal
  const modalComponent = (
    <Modal
      open={openModal}
      title={modalData.title}
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={submitModalForm}
        >
          Submit
        </Button>,
      ]}
    >
      <Form
        form={modalForm}
        layout="vertical"
        name="userForm"
        onFinish={createListItem}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          name="name"
          label={modalData.fieldLabel}
          rules={[
            {
              required: true,
              message: "Please input a name!",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please input a description!",
            },
          ]}
        >
          <Input.TextArea
            showCount
            maxLength={2000}
            autoSize={{ minRows: 5, maxRows: 8 }}
          />
        </Form.Item>
        <Form.Item
          name="price"
          label="Price"
          rules={[
            {
              required: true,
              message: "Price is Required!",
            },
          ]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="is_available"
          label="Is Available"
          valuePropName="checked"
        >
          <Checkbox onChange={isAvailable} />
        </Form.Item>
      </Form>
    </Modal>
  );

  const CreateItemButton = ({ btnText }) => {
    return (
      <div className="create-item">
        <Button type="primary" onClick={showModal}>
          {btnText}
        </Button>
      </div>
    );
  };

  const dateFormat = TABLE_DATE_FORMAT;

  const columns = [
    {
      title: "Name",
      key: "ad_name",
      render: (text) => <Link to={`${text.id}`}>{text.name}</Link>,
    },
    {
      title: "Date Created",
      key: "created",
      render: (text) => (
        <>{moment(new Date(text && text.created)).format(dateFormat)}</>
      ),
    },
    {
      title: "Last Modified",
      key: "modified",
      render: (text) => (
        <>{moment(new Date(text && text.modified)).format(dateFormat)}</>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text) => (
        <Space size="middle">
          <button
            onClick={() => {
              navigate(`${text.id}`);
            }}
          >
            <Tooltip title="Edit">
              <FontAwesomeIcon icon={faEdit} />
            </Tooltip>
          </button>
          <button
            onClick={() => {
              const APIURL = `/api/restaurant/extra/${text.id}`;
              deleteListItem(APIURL, text.id);
            }}
          >
            <Tooltip title="Delete">
              <FontAwesomeIcon icon={faTrashAlt} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  const CardListComponent = ({ listItems }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const page = Math.min(currentPage, Math.max(1, Math.ceil(listItems.length / pageSize)));
    const currentItems = listItems.slice((page - 1) * pageSize, page * pageSize);

    return (
      <>
        <Collapse accordion style={{ overflowWrap: "anywhere" }}>
          {currentItems.map((item) => (
            <Collapse.Panel key={item.id} header={<strong>{item.name}</strong>}>
              <p><strong>Name:</strong> <Link to={`${item.id}`}>{item.name}</Link></p>
              <p>
                <strong>Date Created:</strong>{" "}
                {moment(new Date(item.created)).format(dateFormat)}
              </p>
              <p>
                <strong>Last Modified:</strong>{" "}
                {moment(new Date(item.modified)).format(dateFormat)}
              </p>
              <Space size="middle" wrap>
                <Tooltip title="Edit">
                  <Button aria-label={`Edit ${item.name}`} onClick={() => navigate(`${item.id}`)}>
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    aria-label={`Delete ${item.name}`}
                    onClick={() => deleteListItem(`/api/restaurant/extra/${item.id}`)}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </Tooltip>
              </Space>
            </Collapse.Panel>
          ))}
        </Collapse>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={listItems.length}
            onChange={setCurrentPage}
            size="small"
            showSizeChanger={false}
            showLessItems
          />
        </div>
      </>
    );
  };

  const ListComponent = ({ emptyMessage, listItems }) => {
    return (
      <>
        {listItems.length > 0 ? (
          <>
            {screens.lg ? (
              <Table rowKey="id" columns={columns} dataSource={listItems} />
            ) : (
              <CardListComponent listItems={listItems} />
            )}
          </>
        ) : (
          <Empty
            image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            imageStyle={{
              height: 60,
            }}
            description={<span>{emptyMessage}</span>}
          />
        )}
      </>
    );
  };

  return [modalComponent, CreateItemButton, ListComponent];
};
